import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from '@emotion/styled';
import {
  Button,
  Input,
  InputRef,
  Modal,
  Typography,
} from '@robinpowered/ui-kit';
import Close from '@robinpowered/ui-kit-icons/Close';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagnifyingGlass,
  faUpRightFromSquare,
} from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from 'contexts';
import {
  useGlobalSearchForSearchOverlayQuery,
  useOrganizationLocationsForSearchOverlayQuery,
} from 'generated/graphql';
import type { GlobalSearchFilter } from 'generated/graphql';
import { BuildingFilter } from './components/BuildingFilter';
import { CategoryFilter } from './components/CategoryFilter';
import { DeskResult } from './components/DeskResult';
import { SearchEmptyState } from './components/SearchEmptyState';
import { SearchErrorState } from './components/SearchErrorState';
import { SearchLoadingState } from './components/SearchLoadingState';
import { SpaceResult } from './components/SpaceResult';
import { UserResult } from './components/UserResult';
import { usePersistedSearchState } from 'hooks/usePersistedSearchState';
import { SHELL_OVERLAY_Z_INDEX } from 'constants/zIndex';
import type { DeskSearchHit, SpaceSearchHit } from './types';
import { useSearchOverlayAmplitudeTracking } from './hooks/useSearchOverlayAmplitudeTracking';

interface SearchOverlayProps {
  open: boolean;
  onClose: () => void;
  onSelectUser?: (userId: string) => void;
  onSelectMapResource?: () => void;
}

export const SearchOverlay: React.FC<SearchOverlayProps> = ({
  open,
  onClose,
  onSelectUser,
  onSelectMapResource,
}) => {
  const { t } = useTranslation('globalNavigation', {
    keyPrefix: 'searchOverlay',
  });
  const { currentOrg, orgSlug, accessToken } = useAuthContext();
  const navigate = useNavigate();
  const {
    query,
    setQuery,
    selectedLocationId,
    setSelectedLocationId,
    selectedCategory,
    setSelectedCategory,
    clearSearch,
    cachedResults,
    cachedResultsQuery,
    persistSearchResults,
  } = usePersistedSearchState(accessToken, currentOrg?.id);
  const [debouncedQuery, setDebouncedQuery] = useState<string>(query);
  const searchInputRef = useRef<InputRef>(null);
  const resultRowRefs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedQuery(query);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [query]);

  const organizationId = currentOrg?.id;
  const searchQuery = debouncedQuery.trim();

  const searchFilter = useMemo<GlobalSearchFilter | undefined>(() => {
    const filter: GlobalSearchFilter = {};
    if (selectedLocationId) {
      filter.locationIds = [selectedLocationId];
    }
    if (selectedCategory) {
      filter.entityTypes = [selectedCategory];
    }
    return Object.keys(filter).length > 0 ? filter : undefined;
  }, [selectedLocationId, selectedCategory]);

  const { data, error, loading, refetch } =
    useGlobalSearchForSearchOverlayQuery({
      skip: !open || !organizationId || searchQuery.length < 2,
      variables: {
        organizationId: organizationId ?? '',
        query: searchQuery,
        filter: searchFilter,
      },
    });
  const searchResults = data?.globalSearch.results;

  const { data: locationsData, loading: locationsLoading } =
    useOrganizationLocationsForSearchOverlayQuery({
      skip: !open || !organizationId,
      variables: { organizationId: organizationId ?? '' },
    });
  const buildings = locationsData?.getOrganizationById?.locations ?? [];

  useEffect(() => {
    if (searchResults && searchQuery.length >= 2) {
      persistSearchResults(
        searchQuery,
        selectedLocationId,
        selectedCategory,
        searchResults
      );
    }
  }, [
    searchResults,
    searchQuery,
    selectedLocationId,
    selectedCategory,
    persistSearchResults,
  ]);

  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const displayedResults = searchResults ?? cachedResults;
  const displayedResultsSearchTerm = searchResults
    ? searchQuery
    : cachedResultsQuery;
  const hasDisplayableResults = (displayedResults?.length ?? 0) > 0;
  const { trackSearchCanceled, trackSearchCleared, trackSearchResultClicked } =
    useSearchOverlayAmplitudeTracking({
      open,
      query,
      searchTerm: searchQuery,
      selectedLocationId,
      selectedCategory,
      loading,
      error,
      searchResults,
      displayedResultsCount: displayedResults?.length ?? 0,
      displayedResultsSearchTerm,
    });

  const handleClearSearch = useCallback(() => {
    trackSearchCleared();
    clearSearch();
    setDebouncedQuery('');
  }, [clearSearch, trackSearchCleared]);

  const handleCancelSearch = useCallback(() => {
    trackSearchCanceled();
    onClose();
  }, [onClose, trackSearchCanceled]);

  useEffect(() => {
    resultRowRefs.current = resultRowRefs.current.slice(
      0,
      displayedResults?.length ?? 0
    );
  }, [displayedResults]);

  const setResultRowRef = useCallback(
    (index: number, row: HTMLDivElement | null) => {
      resultRowRefs.current[index] = row;
    },
    []
  );

  const focusResultRow = useCallback(
    (event: React.KeyboardEvent<Element>, direction: 'previous' | 'next') => {
      const rows = resultRowRefs.current.filter(
        (row): row is HTMLDivElement => row !== null
      );

      if (rows.length === 0) {
        return;
      }

      event.preventDefault();

      const currentIndex = rows.findIndex(
        (row) => row === document.activeElement
      );
      const delta = direction === 'next' ? 1 : -1;
      let nextIndex = currentIndex + delta;

      if (currentIndex === -1) {
        nextIndex = direction === 'next' ? 0 : rows.length - 1;
      } else {
        nextIndex = Math.max(0, Math.min(nextIndex, rows.length - 1));
      }
      const nextRow = rows[nextIndex];

      nextRow.focus();
      nextRow.scrollIntoView?.({ block: 'nearest' });
    },
    []
  );

  const handleResultNavigationKeyDown = useCallback(
    (event: React.KeyboardEvent<Element>) => {
      if (event.key === 'ArrowDown') {
        focusResultRow(event, 'next');
      }

      if (event.key === 'ArrowUp') {
        focusResultRow(event, 'previous');
      }
    },
    [focusResultRow]
  );

  const showLoadingState =
    loading && searchQuery.length >= 2 && !displayedResults;
  const showErrorState =
    !!error && !loading && searchQuery.length >= 2 && !displayedResults;

  const handleSelectMapResource = useCallback(
    (type: 'seats' | 'spaces', hit: DeskSearchHit | SpaceSearchHit) => {
      if (!orgSlug) {
        return;
      }
      const params = new URLSearchParams();
      params.set('locations', hit.locationId);
      if (hit.levelId) {
        params.set('levels', hit.levelId);
      }
      params.set('csr', JSON.stringify({ type, id: hit.entityId }));
      navigate(`/${orgSlug}/officeV2?${params.toString()}`);
      onClose();
      onSelectMapResource?.();
    },
    [navigate, orgSlug, onClose, onSelectMapResource]
  );

  const showNoResults =
    searchQuery.length >= 2 && !loading && !error && !hasDisplayableResults;

  return (
    <SearchModal
      aria-labelledby="search-overlay-title"
      open={open}
      zIndex={SHELL_OVERLAY_Z_INDEX}
      footer={null}
      closable={false}
      destroyOnHidden={true}
      mask={{ closable: true }}
      centered
      onCancel={handleCancelSearch}
      afterOpenChange={(isOpen) => {
        if (isOpen) {
          searchInputRef.current?.focus();
          searchInputRef.current?.select();
        }
      }}
      width="min(560px, calc(100vw - 32px))"
      styles={searchModalStyles}
      title={
        <VisuallyHiddenTitle id="search-overlay-title">
          {t('dialogTitle')}
        </VisuallyHiddenTitle>
      }
    >
      <SearchBarRow>
        <StyledInput
          aria-label={t('inputLabel')}
          placeholder={t('placeholder')}
          ref={searchInputRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleResultNavigationKeyDown}
          prefix={<FontAwesomeIcon icon={faMagnifyingGlass} />}
        />
        <Button
          type="text"
          onClick={handleClearSearch}
          style={{ paddingInline: 8 }}
        >
          {t('clear')}
        </Button>
        <Button
          type="text"
          icon={<Close />}
          aria-label={t('close')}
          onClick={handleCancelSearch}
          style={{ minWidth: 32, paddingInline: 4 }}
        />
      </SearchBarRow>

      <FilterRow role="group" aria-label={t('filtersLabel')}>
        <BuildingFilter
          buildings={buildings}
          loading={locationsLoading}
          selectedLocationId={selectedLocationId}
          onChange={setSelectedLocationId}
        />
        <CategoryFilter
          selectedCategory={selectedCategory}
          onChange={setSelectedCategory}
        />
      </FilterRow>

      <ScrollArea>
        {searchQuery.length < 2 && (
          <PromptState>
            <SearchIllustration aria-hidden="true" />
            <PromptStateCopy>{t('emptyState')}</PromptStateCopy>
          </PromptState>
        )}

        {showLoadingState && <SearchLoadingState />}

        {showErrorState && <SearchErrorState onRetry={handleRetry} />}

        {showNoResults && <SearchEmptyState />}

        {hasDisplayableResults && (
          <ResultsList
            data-testid="search-overlay-results"
            onKeyDown={handleResultNavigationKeyDown}
          >
            {displayedResults?.map((result, index) => {
              const key = `${result.__typename}-${result.id}`;

              switch (result.__typename) {
                case 'UserSearchHit':
                  return (
                    <UserResult
                      key={key}
                      resultRef={
                        onSelectUser
                          ? (row) => setResultRowRef(index, row)
                          : undefined
                      }
                      result={result}
                      onSelect={
                        onSelectUser
                          ? (userId) => {
                              trackSearchResultClicked(result, index);
                              onSelectUser(userId);
                            }
                          : undefined
                      }
                    />
                  );
                case 'DeskSearchHit':
                  return (
                    <DeskResult
                      key={key}
                      resultRef={
                        orgSlug
                          ? (row) => setResultRowRef(index, row)
                          : undefined
                      }
                      result={result}
                      onSelect={
                        orgSlug
                          ? (desk) => {
                              trackSearchResultClicked(result, index);
                              handleSelectMapResource('seats', desk);
                            }
                          : undefined
                      }
                    />
                  );
                case 'SpaceSearchHit':
                  return (
                    <SpaceResult
                      key={key}
                      resultRef={
                        orgSlug
                          ? (row) => setResultRowRef(index, row)
                          : undefined
                      }
                      result={result}
                      onSelect={
                        orgSlug
                          ? (space) => {
                              trackSearchResultClicked(result, index);
                              handleSelectMapResource('spaces', space);
                            }
                          : undefined
                      }
                    />
                  );
                default:
                  return null;
              }
            })}
          </ResultsList>
        )}
      </ScrollArea>

      <Footer>
        <FooterTip>{t('tip')}</FooterTip>
        <FooterHelp
          href="https://support.robinpowered.com/hc/en-us/articles/46486919138189-Search-your-Workplace-with-Robin"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span>{t('help')}</span>
          <FontAwesomeIcon icon={faUpRightFromSquare} />
        </FooterHelp>
      </Footer>
    </SearchModal>
  );
};

const searchModalStyles = {
  wrapper: {
    padding: 0,
    overflow: 'hidden',
    borderRadius: 10,
  },
  header: {
    padding: 0,
    margin: 0,
  },
  body: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    height: 'min(540px, calc(100vh - 32px))',
  },
} as React.ComponentProps<typeof Modal>['styles'];

const SearchModal = styled(Modal)`
  max-width: calc(100vw - 32px);
  --ant-modal-content-padding: 0;
`;

const VisuallyHiddenTitle = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const SearchBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 64px;
  padding: 16px;
  box-sizing: border-box;
`;

const StyledInput = styled(Input)`
  flex: 1;

  .ant-input-affix-wrapper {
    min-height: 32px;
    border-radius: 6px;
  }
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px 0;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
`;

const PromptState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  padding: 24px 24px 16px;
  text-align: center;
`;

const SearchIllustration = styled.div`
  position: relative;
  width: 72px;
  height: 72px;
  margin-bottom: 18px;

  &::before {
    content: '';
    position: absolute;
    top: 10px;
    left: 13px;
    width: 38px;
    height: 38px;
    border: 1.5px solid #8c8c8c;
    border-radius: 999px;
  }

  &::after {
    content: '';
    position: absolute;
    top: 42px;
    left: 44px;
    width: 22px;
    height: 6px;
    border: 1.5px solid #8c8c8c;
    border-radius: 999px;
    transform: rotate(45deg);
    transform-origin: left center;
  }
`;

const PromptStateCopy = styled(Typography.Paragraph)`
  max-width: 260px;
  margin: 0;
  color: rgba(0, 0, 0, 0.72);
  font-size: 16px;
`;

const ResultsList = styled.div`
  padding: 8px 16px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 42px;
  padding: 8px 16px;
  border-top: 1px solid #f0f0f0;
  box-sizing: border-box;
`;

const FooterHelp = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgba(0, 0, 0, 0.88);
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  text-decoration: none;
`;

const FooterTip = styled(Typography.Text)`
  color: rgba(0, 0, 0, 0.72);
  font-size: 12px;
  line-height: 20px;
`;
